import { useCallback } from 'react';
import Swal from 'sweetalert2';
import { 
  toggleCompetitionLiveApi, 
  updateMatchWinnerApi, 
  resetCompetitionResultsApi,
  updateCompetitionSettingsApi
} from '../services/api';

export function useCompetitionActions({
  selectedCompetition,
  order,
  allAthletes,
  setOrder,
  setFormatSettings,
  onSelectCompetition,
  loadInitialData
}) {

  // --- 1. LANCER / ARRÊTER LE LIVE ---
  const handleToggleLive = useCallback(async (newState) => {
    try {
      Swal.showLoading();
      const updatedComp = await toggleCompetitionLiveApi(selectedCompetition.id, newState);
      onSelectCompetition(updatedComp);
      Swal.fire({
        title: newState ? 'Compétition Lancée !' : 'Compétition en Pause',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (err) {
      Swal.fire('Erreur', err.message, 'error');
    }
  }, [selectedCompetition?.id, onSelectCompetition]);

  // --- 2. VALIDER UN GAGNANT (ET PROPAGER) ---
  const handleUpdateWinner = useCallback(async (match, winnerId, decisionData = null) => {
    try {
      Swal.showLoading();

      // Calcul de la propagation si c'est un tournoi
      let nextMatchInfo = null;
      if (winnerId) {
          const labelRecherche = `Vainqueur V${match.match_ref}`;
          const nextMatch = order.find(m => 
              m.category_name === match.category_name && 
              (m.placeholder1 === labelRecherche || m.placeholder2 === labelRecherche)
          );

          if (nextMatch) {
              nextMatchInfo = {
                  id: nextMatch.id,
                  data: nextMatch.placeholder1 === labelRecherche 
                      ? { athlete1_id: winnerId } 
                      : { athlete2_id: winnerId }
              };
          }
      }

      // On utilise la fonction de api.js qui gère la cascade
      await updateMatchWinnerApi(match.id, winnerId, nextMatchInfo, decisionData);
      await loadInitialData();
      Swal.fire({ icon: 'success', title: 'Résultat enregistré', timer: 1000, showConfirmButton: false });
    } catch (err) {
      console.error(err);
      Swal.fire('Erreur', "Impossible d'enregistrer : " + err.message, 'error');
    }
  }, [order, loadInitialData]);

  // --- 3. RÉINITIALISER LES SCORES ---
  const handleResetCompetition = useCallback(async () => {
    const result = await Swal.fire({
      title: 'Réinitialiser les scores ?',
      text: "L'ordre des matchs restera inchangé.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Oui, réinitialiser'
    });

    if (result.isConfirmed) {
      try {
        Swal.showLoading();
        await resetCompetitionResultsApi(order);
        await loadInitialData();
        Swal.fire('Réinitialisé !', 'Les scores sont effacés.', 'success');
      } catch (err) {
        Swal.fire('Erreur', "Échec du reset : " + err.message, 'error');
      }
    }
  }, [order, loadInitialData]);

  // --- 4. SAUVEGARDER LES PARAMÈTRES (FORMATS) ---
  const handleSaveAllSettings = useCallback(async (allSettings) => {
    setFormatSettings(allSettings); 
    try {
      await updateCompetitionSettingsApi(selectedCompetition.id, allSettings);
      Swal.fire({ icon: 'success', title: 'Paramètres sauvegardés', toast: true, position: 'top-end', timer: 2000, showConfirmButton: false });
    } catch (error) {
      Swal.fire('Erreur', 'Sauvegarde échouée', 'error');
    }
  }, [selectedCompetition?.id, setFormatSettings]);

  return {
    handleToggleLive,
    handleUpdateWinner,
    handleResetCompetition,
    handleSaveAllSettings
  };
}
